import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';
import { getRuntimeConfig } from '../config/runtimeConfig';
import { useSession } from '../session/SessionProvider';
import { WS_EVENTS, type WsErrorPayload } from './contract';
import { createMockStream } from './mockStream';
import type { ConnectionStatus, WeatherFeed, WeatherState } from './types';

/**
 * Transportul datelor live.
 *
 * Frontend-ul nu se conectează niciodată direct la brokerul MQTT — n-ar avea
 * cum fără să expună credențialele în bundle. Se conectează la gateway-ul
 * NestJS, care ține abonamentul MQTT server-side și retransmite doar clienților
 * care prezintă un bilet valid. Biletul călătorește în handshake-ul socket.io
 * (`auth.token`), nu în query string, ca să nu ajungă în logurile de acces.
 */

const IDLE_FEED: WeatherFeed = { state: null, status: 'idle', error: null };

const WeatherContext = createContext<WeatherFeed>(IDLE_FEED);

function isWeatherState(value: unknown): value is WeatherState {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isAuthFailure(message: string): boolean {
  const normalized = message.toLowerCase();
  return (
    normalized.includes('unauthorized') ||
    normalized.includes('forbidden') ||
    normalized.includes('token')
  );
}

export function WeatherProvider({ children }: { children: ReactNode }) {
  const { status: sessionStatus, error: sessionError, getToken, tokenVersion, renew } = useSession();
  const [feed, setFeed] = useState<WeatherFeed>(IDLE_FEED);

  useEffect(() => {
    const config = getRuntimeConfig();

    if (config.demo) {
      setFeed({ state: null, status: 'connecting', error: null });
      return createMockStream((state) => setFeed({ state, status: 'connected', error: null }));
    }

    if (sessionStatus === 'error') {
      setFeed({ state: null, status: 'error', error: sessionError });
      return;
    }
    if (sessionStatus !== 'ready') {
      setFeed({ state: null, status: 'connecting', error: null });
      return;
    }

    const setStatus = (status: ConnectionStatus, error: string | null = null) =>
      setFeed((prev) => ({ ...prev, status, error }));

    setStatus('connecting');

    const socket: Socket = io(config.socketUrl || undefined, {
      path: config.socketPath,
      transports: ['websocket'],
      withCredentials: true,
      auth: { token: getToken() },
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10_000,
    });

    socket.on('connect', () => {
      setStatus('connected');
      // Fallback dacă serverul nu trimite din proprie inițiativă la conectare.
      socket.emit(WS_EVENTS.sync, null, (snapshot: unknown) => {
        if (isWeatherState(snapshot)) {
          setFeed({ state: snapshot, status: 'connected', error: null });
        }
      });
    });

    socket.on(WS_EVENTS.state, (payload: unknown) => {
      if (isWeatherState(payload)) {
        setFeed({ state: payload, status: 'connected', error: null });
      }
    });

    socket.on(WS_EVENTS.error, (payload: WsErrorPayload) => {
      setStatus('error', payload?.message ?? 'Eroare raportată de server.');
    });

    socket.on('disconnect', (reason: string) => {
      // `io server disconnect` = serverul a închis intenționat (bilet invalidat).
      if (reason === 'io server disconnect') {
        setStatus('reconnecting', 'Se reînnoiește accesul…');
        renew();
        return;
      }
      setStatus('reconnecting', 'Conexiune pierdută. Se reconectează…');
    });

    socket.on('connect_error', (err: Error) => {
      if (isAuthFailure(err.message)) {
        setStatus('reconnecting', 'Bilet expirat. Se cere unul nou…');
        socket.close();
        renew();
        return;
      }
      setStatus('reconnecting', 'Serverul nu răspunde. Se reîncearcă…');
    });

    return () => {
      socket.removeAllListeners();
      socket.close();
    };
    // `tokenVersion` se schimbă la fiecare bilet nou → reconectăm cu el.
  }, [sessionStatus, sessionError, tokenVersion, getToken, renew]);

  return <WeatherContext.Provider value={feed}>{children}</WeatherContext.Provider>;
}

export function useWeatherFeed(): WeatherFeed {
  return useContext(WeatherContext);
}
